// -----------------------------------------------------------------------------
//
// This file is the copyrighted property of Tableau Software and is protected
// by registered patents and other applicable U.S. and international laws and
// regulations.
//
// Unlicensed use of the contents of this file is prohibited. Please refer to
// the NOTICES.txt file for further details.
//
// -----------------------------------------------------------------------------

import { SagaIterator } from 'redux-saga';
import { all, call, put } from 'redux-saga/effects';

import {
  createMultipleItemResultNotificationValues,
  MultipleItemResultNotificationValues
} from '~commonInfra/contentActions/ActionMessages';
import { ContentActionNotification} from '~commonInfra/contentActions/ContentActionNotification';
import {
  IErrorItem, MultipleItemResult
} from '~commonInfra/contentActions/MultipleItemResult';
import { CommonErrorCode, ExtractCreationErrorCode } from '~commonInfra/utils/ErrorCodes';
import { Set } from '~commonInfra/utils/Set';

import { DatasourceContentType, WorkbookContentType } from '../../../src/spacesPlatform/models/ContentTypes';
import { IContentInfo } from '../../../src/spacesPlatform/models/IContentInfo';
import { requestFailed } from '../../bootstrap/redux/actions/RequestFailedAction';
import { Messages } from '../../codegen/Localize';
import { DataSources } from '../../datasource/resources/Datasources';
import { IResult } from '../../httpClient/serverApi/types/IResponse';
import { requestSpaceContents } from '../../spacesPlatform/actions/RequestSpaceContentsActions';
import { reloadSpaceIfUpdatingCurrentSpace } from '../../spacesPlatform/sagas/content/RefreshContentSagas';
import { replaceDatasourceIdWithConnectionIdForMixedContent } from '../../spacesPlatform/sagas/SpacesSagaHelpers';
import { Workbooks } from '../../workbook/resources/Workbooks';
import { CancelExtractTaskAction  } from '../actions/CancelExtractTaskAction';
import { EncryptionActionType } from '../actions/encryptContent/EncryptContentAction';
import { encryptDataSources, encryptWorkbooks, processResult, showNotification } from './EncryptContentSaga';

export function cancelExtractCreationOnDatasource(datasources: Set<IContentInfo>): Promise<IResult> {
  if(datasources.size() < 1) {
    return null;
  }
  return DataSources.cancelExtractCreationOnDatasources(datasources.map(datasource => datasource.id));
}

export function cancelExtractCreationOnWorkbook(workbooks: Set<IContentInfo>): Promise<IResult> {
  if(workbooks.size() < 1) {
    return null;
  }
  return Workbooks.cancelExtractCreationOnWorkbooks(workbooks.map(workbook => workbook.id));
}

export function cancelExtractEncryptionOnDatasource(datasources: Set<IContentInfo>): Promise<IResult> {
  if(datasources.size() < 1) {
    return null;
  }
  return DataSources.cancelExtractCreationOnDatasources(datasources.map(datasource => datasource.id));
}

export function cancelExtractEncryptionOnWorkbook(datasources: Set<IContentInfo>): Promise<IResult> {
  if(datasources.size() < 1) {
    return null;
  }
  return DataSources.cancelExtractCreationOnDatasources(datasources.map(datasource => datasource.id));
}

export function processCancelExtractCreationResult(dataSourcesResult: IResult, workbooksResult: IResult): IResult {
  let result: IResult;
  const errors = [];
  if(dataSourcesResult != null) {
    errors.push(...dataSourcesResult.errors);
  }

  if(workbooksResult != null) {
    errors.push(...workbooksResult.errors);
  }

  result = {
    errors: errors
  };
  return result;
}

export function showExtractCreationNotification(result: IResult, content: IContentInfo[]) {
  const notification: MultipleItemResultNotificationValues = createMultipleItemResultNotificationValues(
    new MultipleItemResult(Set.fromIdItemArray(content), result),
    {
      describeSuccessSummary: () => {
        return Messages.cancelExtractCreationSuccessful();
      },
      describeErrorSummary: () => {
        return Messages.cancelExtractCreationFailed();
      },
      describeErrorDetail: (errorItem: IErrorItem<IContentInfo>) => {
        return {
          itemName: errorItem.item.name,
          errorMessage: getErrorMessageFor(errorItem.error.code)
        };
      }
    }
  );
  ContentActionNotification.displayItemizedToastsForResult(notification);
}

export function getErrorMessageFor(errorCode: number): string {
  switch (errorCode) {
    case CommonErrorCode.CallingUserInsufficientPermissions:
      return Messages.permission_denied();
    case CommonErrorCode.UnexpectedErrorOccurred:
        return Messages.cancelExtractCreationFailed();
    case ExtractCreationErrorCode.BackgroundJobNotFound:
      return Messages.cancelExtractCreationFailedNotFound();
    default:
      return Messages.errorUnknown();
  }
}

export function* processExtractContentAction(action: CancelExtractTaskAction): SagaIterator {
  try {
    const mutableContentCopy: IContentInfo[] = yield call(replaceDatasourceIdWithConnectionIdForMixedContent, action.contentArray);
    const datasources: Set<IContentInfo> = Set.fromIdItemArray(mutableContentCopy.filter(i => i.contentType === DatasourceContentType.name));
    const workbooks: Set<IContentInfo> = Set.fromIdItemArray(mutableContentCopy.filter(i => i.contentType === WorkbookContentType.name));
    if (action.cancelCreation) {
      const [dataSourcesResult, workbooksResult] = (yield all([
        call(cancelExtractCreationOnDatasource, datasources),
        call(cancelExtractCreationOnWorkbook, workbooks),
      ])) as any; // TFS 1116084 - need to upgrade redux-saga in order to remove 'as any'
      const result = processCancelExtractCreationResult(dataSourcesResult, workbooksResult);
      yield call(showExtractCreationNotification, result, mutableContentCopy);
    } else {
      const encryptionActionType = action.encryptionActionType ? action.encryptionActionType : EncryptionActionType.decryptExtracts;
      const [dataSourcesResult, workbooksResult] = (yield all([
        call(encryptDataSources, datasources, encryptionActionType),
        call(encryptWorkbooks, workbooks, encryptionActionType),
      ])) as any; // TFS 1116084 - need to upgrade redux-saga in order to remove 'as any'
      const result = processResult(dataSourcesResult, workbooksResult, null);
      yield call(showNotification, result, mutableContentCopy, encryptionActionType);
    }
    const didReload = yield call(reloadSpaceIfUpdatingCurrentSpace, action.contentArray);
    if (!didReload) {
      yield put(requestSpaceContents());
    }
  } catch (e) {
    yield put(requestFailed(e, Messages.errorUnknown()));
  }
}
